import { m } from 'framer-motion';
import { forwardRef, useState } from 'react';
// @mui
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
// components
import { MotionViewport, varFade } from 'src/components/animate';
// utils
import { countries } from 'src/utils/country';
import { API_BASE_URL } from 'src/config-global';

// ----------------------------------------------------------------------

// Validation schema for form fields
const validationSchema = {
  firstName: {
    required: true,
    label: 'First name',
  },
  lastName: {
    required: true,
    label: 'Last name',
  },
  email: {
    required: true,
    label: 'Email',
    pattern: /\S+@\S+\.\S+/,
    patternMessage: 'Please enter a valid email',
  },
  company: {
    required: true,
    label: 'Company name',
  },
  phone: {
    required: true,
    label: 'Phone number',
  },
  role: {
    required: true,
    label: 'Job role',
  },
  roleName: {
    required: (formData) => formData.role === 'Other',
    label: 'Role specification',
    dependsOn: 'role',
  },
  message: {
    required: false,
    label: 'Message',
  },
};

// Generic validation function
const validateField = (fieldName, value, formData = {}) => {
  const field = validationSchema[fieldName];
  if (!field) return '';

  // Check if field is conditionally required
  if (field.required && typeof field.required === 'function') {
    if (!field.required(formData)) return '';
  } else if (field.required && !value.trim()) {
    return `${field.label} is required`;
  }

  // Check pattern validation
  if (field.pattern && !field.pattern.test(value)) {
    return field.patternMessage || `Invalid ${field.label.toLowerCase()}`;
  }

  return '';
};

// Validate entire form
const validateForm = (formData) => {
  const errors = {};

  Object.keys(validationSchema).forEach((fieldName) => {
    const error = validateField(fieldName, formData[fieldName], formData);
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};

const ContactForm = forwardRef((ref) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    countryCode: '+1',
    role: 'Fnb Operator',
    roleName: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Real-time validation
    const error = validateField(field, value, { ...formData, [field]: value });
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleCountryChange = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        countryCode: `+${newValue.phone}`,
      });
    }
  };

  // Map form role values to API expected format
  const mapRoleToAPI = (role) => {
    const roleMap = {
      'Fnb Operator': 'fnb_operator',
      Supplier: 'supplier',
      Distributor: 'distributor',
      Other: 'other',
    };
    return roleMap[role] || 'fnb_operator';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare request body with snake_case format
      const requestBody = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        company_name: formData.company,
        email: formData.email,
        country_code: formData.countryCode,
        phone_number: formData.phone,
        role: mapRoleToAPI(formData.role),
        message: formData.message || '',
      };

      const response = await fetch(`${API_BASE_URL}/v1/web/request-demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit form. Please try again.');
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          phone: '',
          countryCode: '+1',
          role: 'Fnb Operator',
          roleName: '',
          message: '',
        });
        setErrors({});
      }, 3000);
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setErrors({
        submit: error.message || 'An error occurred. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    const newErrors = validateForm(formData);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <Stack ref={ref} component={MotionViewport} spacing={5}>
      <m.div variants={varFade().inUp}>
        <Typography variant="h3">
          Feel free to contact us. <br />
        </Typography>
      </m.div>

      {submitSuccess && (
        <m.div variants={varFade().inUp}>
          <Alert severity="success">
            Thank you! We&apos;ve received your message and will get back to you within 24 hours.
          </Alert>
        </m.div>
      )}

      {errors.submit && (
        <m.div variants={varFade().inUp}>
          <Alert severity="error">{errors.submit}</Alert>
        </m.div>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* First Name and Last Name Row */}
          <m.div variants={varFade().inUp}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  size="small"
                />
              </Grid>
            </Grid>
          </m.div>

          {/* Company Name */}
          <m.div variants={varFade().inUp}>
            <TextField
              required
              fullWidth
              name="company"
              label="Company Name"
              value={formData.company}
              onChange={handleInputChange('company')}
              error={!!errors.company}
              helperText={errors.company}
              size="small"
            />
          </m.div>

          {/* Email */}
          <m.div variants={varFade().inUp}>
            <TextField
              required
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              size="small"
            />
          </m.div>

          {/* Country Code and Phone Number Row */}
          <m.div variants={varFade().inUp}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={countries}
                  autoHighlight
                  getOptionLabel={(option) => `${option.code} (+${option.phone})`}
                  renderOption={(props, option) => (
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                        alt=""
                      />
                      {option.code} (+{option.phone})
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Country Code"
                      size="small"
                      value={formData.countryCode}
                      onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    />
                  )}
                  onChange={handleCountryChange}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  required
                  fullWidth
                  name="phone"
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  size="small"
                />
              </Grid>
            </Grid>
          </m.div>

          {/* Role Selection */}
          <m.div variants={varFade().inUp}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleInputChange('role')}
                error={!!errors.role}
              >
                <MenuItem value="Fnb Operator">Fnb Operator</MenuItem>
                <MenuItem value="Supplier">Supplier</MenuItem>
                <MenuItem value="Distributor">Distributor</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </m.div>

          {/* Conditional Role Specification */}
          {formData.role === 'Other' && (
            <m.div variants={varFade().inUp}>
              <TextField
                fullWidth
                name="roleName"
                label="Please specify your role"
                value={formData.roleName}
                onChange={handleInputChange('roleName')}
                error={!!errors.roleName}
                helperText={errors.roleName}
                size="small"
              />
            </m.div>
          )}

          {/* Message */}
          <m.div variants={varFade().inUp}>
            <TextField
              fullWidth
              name="message"
              label="Enter your message here."
              multiline
              rows={4}
              value={formData.message}
              onChange={handleInputChange('message')}
              size="small"
              placeholder="Tell us more about your inquiry or any specific questions you have..."
            />
          </m.div>

          {/* Submit Button */}
          <m.div variants={varFade().inUp}>
            <Button
              type="submit"
              size="large"
              variant="contained"
              disabled={isSubmitting || !isFormValid()}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
              sx={{ width: 'fit-content' }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Now'}
            </Button>
          </m.div>
        </Stack>
      </form>
    </Stack>
  );
});

ContactForm.displayName = 'ContactForm';

export default ContactForm;
